
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import {
  HomePageWidget,
  Movie,
  MyChannel,
  TvShow,
  VoDGenre
} from "@/services/apiService/api.types";
import apiStorageInterface from "@/services/apiStorageInterface";
import {
  HomePageGetter,
  HomePageMutation,
  HomePageWidgetType,
  IndexData,
  SlideIndexMapParams
} from "@/components/homePage/types";
import {
  arrowKeysPreventDefault,
  filterFavorites,
  scrollToPage
} from "@/helpers/utils";
import { FavoriteStatus, VoDType } from "@/helpers/constants";
import HomePageWidgetSlider from "@/components/homePage/components/HomePageWidgetSlider.vue";
import SpatialNavigation from "spatial-navigation-js";
import { keyCodeFinder } from "@/services/keyCodeService/keyCodeService";
import { KeySet } from "@/services/keyCodeService/keyCode.types";
import { Getter, Mutation } from "vuex-class";
import { Namespace } from "@/store/types";

@Component({
  components: { HomePageWidgetSlider }
})
export default class HomePageWidgetSection extends Vue {
  @Prop() readonly homePageWidgets!: HomePageWidget[];
  @Prop() readonly activeSlideIndex!: number;

  @Mutation(HomePageMutation.setWidgetKeyControl, {
    namespace: Namespace.HomePage
  })
  setWidgetKeyControl!: (status: boolean) => void;

  @Mutation(HomePageMutation.setActiveSlideIndexMap, {
    namespace: Namespace.HomePage
  })
  setActiveSlideIndexMap!: (params?: SlideIndexMapParams) => void;

  @Mutation(HomePageMutation.resetActiveSlideIndexMap, {
    namespace: Namespace.HomePage
  })
  resetActiveSlideIndexMap!: () => void;

  @Getter(HomePageGetter.GetWidgetKeyControl, {
    namespace: Namespace.HomePage
  })
  getWidgetKeyControl!: boolean;

  @Getter(HomePageGetter.GetActiveSlideIndexMap, {
    namespace: Namespace.HomePage
  })
  getActiveSlideIndexMap!: Array<number>;

  @Getter(HomePageGetter.GetPlayedWidgetIndex, {
    namespace: Namespace.HomePage
  })
  getPlayedWidgetIndex?: IndexData;

  widgetData: Array<HomePageWidgetType> = [];
  widgetIndexes: number[] = [];

  activeWidgetIndex = 0;

  smallSwiper = false;

  scrollFuncRef = scrollToPage;

  created(): void {
    window.addEventListener("resize", () => {
      this.checkWindowWidth();
    });
    // setting initial status
    this.checkWindowWidth();
  }

  async mounted(): Promise<void> {
    //fetching data for widgets
    for (const [index, widget] of this.homePageWidgets.entries()) {
      let widgetElements: HomePageWidgetType = [];

      if (widget.channel_category !== null) {
        let categories = await apiStorageInterface.fetchSortedCategories();

        Object.entries(categories).forEach((value) => {
          if (value[1].id === widget.channel_category) {
            widgetElements =
              value[1].channels && value[1].channels.length > 0
                ? value[1].channels
                : [];
          }
        });

        if (widget.channel_favorites === FavoriteStatus.Active) {
          widgetElements = filterFavorites<MyChannel>(
            widgetElements as MyChannel[]
          );
        }

        if (widgetElements.length > 0) {
          //initialise active slide index map element
          this.setActiveSlideIndexMap({ addItem: true });

          this.widgetIndexes.push(index);
          this.widgetData.push(widgetElements);
        }
      }
      if (widget.buffet_category !== null) {
        let buffetCategories =
          await apiStorageInterface.fetchSortedBuffetCategories();

        Object.entries(buffetCategories).forEach((value) => {
          if (value[1].id === widget.buffet_category) {
            widgetElements = value[1].epgList ? value[1].epgList : [];
          }
        });
        if (widgetElements.length > 0) {
          //initialise active slide index map element
          this.setActiveSlideIndexMap({ addItem: true });

          this.widgetIndexes.push(index);
          this.widgetData.push(widgetElements);
        }
      }
      if (widget.vod_category !== null) {
        let vodItemsByGenre: VoDGenre[] = [];
        switch (widget.vod_type) {
          case VoDType.TvShow:
            vodItemsByGenre = await apiStorageInterface.fetchTvShowsByGenre();
            break;
          case VoDType.Movie:
            vodItemsByGenre = await apiStorageInterface.fetchMoviesByGenre();
            break;
        }

        for (const vodGenre of vodItemsByGenre) {
          if (vodGenre.id === widget.vod_category) {
            widgetElements = vodGenre.latestTvShows
              ? vodGenre.latestTvShows
              : vodGenre.latestMovies
              ? vodGenre.latestMovies
              : [];

            if (widget.vod_favorites === FavoriteStatus.Active) {
              widgetElements = await this.vodGenreFavorites(
                vodGenre.latestTvShows
                  ? vodGenre.latestTvShows
                  : vodGenre.latestMovies
                  ? vodGenre.latestMovies
                  : [],
                widget.vod_type
              );
            }
          }
        }

        if (widgetElements.length > 0) {
          //initialise active slide index map element
          this.setActiveSlideIndexMap({ addItem: true });

          this.widgetIndexes.push(index);
          this.widgetData.push(widgetElements);
        }
      }
    }

    if (this.getPlayedWidgetIndex) {
      this.activeWidgetIndex = this.getPlayedWidgetIndex.valueIndex;
      this.setActiveSlideIndexMap({
        addItem: false,
        slideIndex: {
          valueIndex: this.getPlayedWidgetIndex.valueIndex,
          value: this.getPlayedWidgetIndex.value
        }
      });
    }
  }

  async handleHomeWidgetActions(event: KeyboardEvent): Promise<void> {
    //preventing page scroll with arrow keys when card controls are selected
    arrowKeysPreventDefault(event);
    if (this.getWidgetKeyControl) {
      SpatialNavigation.pause();
      switch (keyCodeFinder(event.keyCode)) {
        case KeySet.KEY_LEFT:
          if (this.getActiveSlideIndexMap[this.activeWidgetIndex] === 0) {
            this.setWidgetKeyControl(false);
            SpatialNavigation.resume();
            SpatialNavigation.focus(".sidebar .focusable");
            return;
          }
          if (this.getActiveSlideIndexMap[this.activeWidgetIndex] > 0) {
            this.setActiveSlideIndexMap({
              addItem: false,
              slideIndex: {
                valueIndex: this.activeWidgetIndex,
                value: --this.getActiveSlideIndexMap[this.activeWidgetIndex]
              }
            });
            SpatialNavigation.focus(
              "#widget" +
                this.activeWidgetIndex +
                "slide" +
                this.getActiveSlideIndexMap[this.activeWidgetIndex]
            );
            return;
          }

          break;
        case KeySet.KEY_RIGHT:
          if (
            this.getActiveSlideIndexMap[this.activeWidgetIndex] <
            this.widgetData[this.activeWidgetIndex].length - 1
          ) {
            this.setActiveSlideIndexMap({
              addItem: false,
              slideIndex: {
                valueIndex: this.activeWidgetIndex,
                value: ++this.getActiveSlideIndexMap[this.activeWidgetIndex]
              }
            });
            SpatialNavigation.focus(
              "#widget" +
                this.activeWidgetIndex +
                "slide" +
                this.getActiveSlideIndexMap[this.activeWidgetIndex]
            );
            return;
          }
          break;
        case KeySet.KEY_UP:
          if (this.activeWidgetIndex === 0) {
            if (this.activeSlideIndex > 0) {
              SpatialNavigation.focus("#id" + this.activeSlideIndex);
              this.scrollFuncRef("id" + this.activeSlideIndex, "end");
              this.setWidgetKeyControl(false);
              event.stopImmediatePropagation();
              return;
            }

            SpatialNavigation.focus("#id0");
            this.scrollFuncRef("id0", "end");
            this.setWidgetKeyControl(false);
            event.stopImmediatePropagation();
            return;
          }
          if (this.activeWidgetIndex > 0) {
            this.activeWidgetIndex--;
            SpatialNavigation.focus(
              "#widget" +
                this.activeWidgetIndex +
                "slide" +
                this.getActiveSlideIndexMap[this.activeWidgetIndex]
            );
            this.scrollFuncRef(
              "widget" +
                this.activeWidgetIndex +
                "slide" +
                this.getActiveSlideIndexMap[this.activeWidgetIndex]
            );
          }
          break;
        case KeySet.KEY_DOWN:
          if (this.activeWidgetIndex < this.homePageWidgets.length - 1) {
            this.activeWidgetIndex++;
            SpatialNavigation.focus(
              "#widget" +
                this.activeWidgetIndex +
                "slide" +
                this.getActiveSlideIndexMap[this.activeWidgetIndex]
            );
            this.scrollFuncRef(
              "widget" +
                this.activeWidgetIndex +
                "slide" +
                this.getActiveSlideIndexMap[this.activeWidgetIndex]
            );
          }
          break;
      }
    }
  }

  async vodGenreFavorites(
    vods: Movie[] | TvShow[],
    vodType: VoDType
  ): Promise<Movie[] | TvShow[]> {
    let vodFavorites: Movie[] | TvShow[] = [];
    switch (vodType) {
      case VoDType.TvShow:
        vodFavorites = await apiStorageInterface.fetchFavoriteTvShows();
        return (vods as TvShow[]).filter((x) =>
          (vodFavorites as TvShow[]).some((y) => x.id === y.id)
        );
      case VoDType.Movie:
        vodFavorites = await apiStorageInterface.fetchFavoriteMovies();

        return (vods as Movie[]).filter((x) =>
          (vodFavorites as Movie[]).some((y) => x.id === y.id)
        );
    }
  }

  @Watch("getWidgetKeyControl")
  handlePositionChange(): void {
    if (this.getWidgetKeyControl) {
      this.activeWidgetIndex = 0;
    }
  }

  checkWindowWidth(): void {
    this.smallSwiper = window.innerWidth <= 1200;
  }

  beforeDestroy(): void {
    this.resetActiveSlideIndexMap();
  }
}
